import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	Backdrop,
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Fade,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Modal,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	Close as CloseIcon,
	ContentCopy as CopyIcon,
	DescriptionOutlined as DescriptionIcon,
	VisibilityOutlined as EyeIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import ReactJson from "react-json-view";
import { ToastContent } from "../../../../shared/components";
import { useToast } from "../../../../shared/hooks";
import { Logs } from "../../../../shared/interfaces";
import { analysisService } from "../../../../shared/services/api/analysis/analysisService";
import { returnMessageError } from "../../../../shared/utils/returnMessageError";

interface ModalLogsAnalysisProps {
	isOpen: boolean;
	onClose: () => void;
	id: string;
}

const PAGE_SIZE = 25;

export const ModalLogsAnalysis: React.FC<ModalLogsAnalysisProps> = ({ isOpen, onClose, id }) => {
	const [allLogs, setAllLogs] = useState<Logs[]>([]);
	const [filteredLogs, setFilteredLogs] = useState<Logs[]>([]);
	const [visibleLogs, setVisibleLogs] = useState<Logs[]>([]);
	const [eventTypes, setEventTypes] = useState<string[]>([]);
	const [userNames, setUserNames] = useState<string[]>([]);
	const [selectedEventType, setSelectedEventType] = useState<string>("todos");
	const [selectedUser, setSelectedUser] = useState<string>("todos");
	const [isLoading, setIsLoading] = useState(false);

	const [openPayload, setOpenPayload] = useState(false);
	const [payloadContent, setPayloadContent] = useState("");
	const [payloadValue, setPayloadValue] = useState<unknown>(null);
	const [payloadId, setPayloadId] = useState<string>("");

	const loaderRef = useRef<HTMLDivElement>(null);
	const toast = useToast();

	const handleCopy = (content: string) => {
		navigator.clipboard.writeText(content);
		toast.present({
			type: "success",
			position: "top-right",
			messageOrContent: <ToastContent title="Copiado!" content="Payload copiado." />,
		});
	};

	const looksLikeJson = (text: string) => {
		const t = text.trim();
		if (!t) return false;
		const first = t.charAt(0);
		const second = t.charAt(1);
		const last = t.charAt(t.length - 1);
		const penultimate = t.charAt(t.length - 2);

		const startsOk = first === "{" || first === "[" || (first === '"' && (second === "{" || second === "["));
		const endsOk = last === "}" || last === "]" || (last === '"' && (penultimate === "}" || penultimate === "]"));
		return startsOk && endsOk;
	};

	const tryParsePossiblyEncodedJson = (text: string): unknown | undefined => {
		const t = text.trim();
		if (!t) return undefined;
		if (!looksLikeJson(t) && !t.includes("\\\"") && !t.includes("\\\\")) return undefined;

		const tryParse = (s: string): unknown | undefined => {
			try {
				return JSON.parse(s);
			} catch {
				return undefined;
			}
		};

		// 1) JSON direto ("{...}" / {...} / [...])
		const parsed = tryParse(t);
		if (parsed !== undefined) {
			// Caso venha como string JSON dentro de string JSON
			if (typeof parsed === "string") {
				const inner = parsed.trim();
				const parsedInner = tryParse(inner);
				if (parsedInner !== undefined) return parsedInner;
			}
			return parsed;
		}

		// 2) Caso clássico vindo com escapes: [{\"id\":...}] (backend serializou JSON como string)
		const unescapedQuotes = t.replace(/\\\"/g, '"');
		const parsedUnescaped = tryParse(unescapedQuotes);
		if (parsedUnescaped !== undefined) return parsedUnescaped;

		// 3) Tentativa extra: reduzir \\ (dupla barra) antes de remover aspas escapadas
		const reducedBackslashes = t.replace(/\\\\/g, "\\").replace(/\\\"/g, '"');
		const parsedReduced = tryParse(reducedBackslashes);
		if (parsedReduced !== undefined) return parsedReduced;

		return undefined;
	};

	const normalizeJsonLikeStrings = (value: unknown): unknown => {
		if (typeof value === "string") {
			const parsed = tryParsePossiblyEncodedJson(value);
			if (parsed !== undefined && (typeof parsed === "object" || Array.isArray(parsed))) {
				return normalizeJsonLikeStrings(parsed);
			}
			return value;
		}

		if (Array.isArray(value)) {
			return value.map((item) => normalizeJsonLikeStrings(item));
		}

		if (value && typeof value === "object") {
			const record = value as Record<string, unknown>;
			const normalized: Record<string, unknown> = {};
			Object.keys(record).forEach((key) => {
				normalized[key] = normalizeJsonLikeStrings(record[key]);
			});
			return normalized;
		}

		return value;
	};

	const handleOpenPayload = (payload: unknown, logId: string) => {
		const normalized = normalizeJsonLikeStrings(payload ?? {});
		setPayloadValue(normalized);
		setPayloadContent(JSON.stringify(normalized ?? {}, null, 2));
		setPayloadId(logId);
		setOpenPayload(true);
	};

	const handleClosePayload = () => {
		setOpenPayload(false);
		setPayloadContent("");
		setPayloadValue(null);
		setPayloadId("");
	};

	const applyFilters = useCallback(() => {
		let result = [...allLogs];

		if (selectedEventType !== "todos") {
			result = result.filter((log) => log.eventType === selectedEventType);
		}

		if (selectedUser !== "todos") {
			result = result.filter((log) => log.userName === selectedUser);
		}

		result.sort((a, b) => dayjs(b.createdDate).unix() - dayjs(a.createdDate).unix());

		setFilteredLogs(result);
		setVisibleLogs(result.slice(0, PAGE_SIZE));
	}, [allLogs, selectedEventType, selectedUser]);

	const handleFilterChange = (type: "event" | "user", value: string) => {
		if (type === "event") setSelectedEventType(value);
		if (type === "user") setSelectedUser(value);
	};

	useEffect(() => {
		applyFilters();
	}, [selectedEventType, selectedUser, applyFilters]);

	useEffect(() => {
		if (!isOpen) return;

		const fetchLogs = async () => {
			setIsLoading(true);
			try {
				const result = await analysisService.getLogs(id);
				if (result instanceof Error) throw result;

				const ordered = [...result].sort((a, b) => dayjs(b.createdDate).unix() - dayjs(a.createdDate).unix());
				const types = Array.from(new Set(result.map((log) => log.eventType).filter(Boolean))) as string[];
				const users = Array.from(
					new Set(result.map((log: Logs) => log.userName).filter((name): name is string => typeof name === "string" && !!name))
				);

				setEventTypes(types);
				setUserNames(users);
				setAllLogs(ordered);
				setFilteredLogs(ordered);
				setVisibleLogs(ordered.slice(0, PAGE_SIZE));
				setSelectedEventType("todos");
				setSelectedUser("todos");
			} catch (err) {
				const { titleMessage, contentMessage } = returnMessageError(err);
				toast.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
				});
			} finally {
				setIsLoading(false);
			}
		};

		fetchLogs();
		// Dependências intencionalmente limitadas para evitar loop caso a instância de `toast` não seja estável.
	}, [isOpen, id]);

	const loadMoreLogs = useCallback(() => {
		setVisibleLogs((prev) => {
			if (prev.length >= filteredLogs.length) return prev;
			const next = filteredLogs.slice(prev.length, prev.length + PAGE_SIZE);
			if (next.length === 0) return prev;
			return [...prev, ...next];
		});
	}, [filteredLogs]);

	useEffect(() => {
		if (!isOpen) return;
		const observer = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting) loadMoreLogs();
		});
		if (loaderRef.current) observer.observe(loaderRef.current);
		return () => observer.disconnect();
	}, [isOpen, loadMoreLogs]);

	return (
		<>
			<Modal open={isOpen} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }} slotProps={{ backdrop: { timeout: 500 } }}>
				<Fade in={isOpen}>
					<Box
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							width: "80%",
							maxHeight: "90vh",
							bgcolor: "background.paper",
							borderRadius: 2,
							boxShadow: 24,
							p: 3,
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Box sx={{ position: "sticky", top: 0, zIndex: 2, backgroundColor: "white", py: 1, borderBottom: "1px solid #e0e0e0" }}>
							<Box display="flex" justifyContent="space-between" alignItems="center">
								<Box display="flex" alignItems="center" gap={2}>
									<DescriptionIcon sx={{ fontSize: 30, color: "primary.main" }} />
									<Typography variant="h5" fontWeight={400} color="primary.main">
										Logs da Análise ({filteredLogs.length} {filteredLogs.length === 1 ? "registro" : "registros"})
									</Typography>
								</Box>
								<IconButton onClick={onClose}>
									<CloseIcon />
								</IconButton>
							</Box>

							<Box mt={2} display="flex" gap={2} flexWrap="wrap">
								<FormControl size="small" sx={{ minWidth: 180 }}>
									<InputLabel>Evento</InputLabel>
									<Select value={selectedEventType} label="Evento" onChange={(e) => handleFilterChange("event", e.target.value)}>
										<MenuItem value="todos">Todos</MenuItem>
										{eventTypes.map((type) => (
											<MenuItem key={type} value={type}>
												{type}
											</MenuItem>
										))}
									</Select>
								</FormControl>

								<FormControl size="small" sx={{ minWidth: 180 }}>
									<InputLabel>Usuário</InputLabel>
									<Select value={selectedUser} label="Usuário" onChange={(e) => handleFilterChange("user", e.target.value)}>
										<MenuItem value="todos">Todos</MenuItem>
										{userNames.map((name) => (
											<MenuItem key={name} value={name}>
												{name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Box>
						</Box>

						<Box sx={{ mt: 2, flex: 1 }}>
							{isLoading ? (
								<Box display="flex" justifyContent="center" py={4}>
									<CircularProgress />
								</Box>
							) : (
								<TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3, maxHeight: "60vh", overflow: "auto" }}>
									<Table size="small">
										<TableBody>
											<TableRow sx={{ backgroundColor: "#f5f5f5" }}>
												{[
													"Data Criada",
													"Tipo Evento",
													"Nome Tabela",
													"Referência",
													"Usuário ID",
													"Usuário Nome",
													"Payload",
												].map((label) => (
													<TableCell key={label} sx={{ fontWeight: 600, color: "#333" }}>
														{label}
													</TableCell>
												))}
											</TableRow>

											{visibleLogs.length === 0 ? (
												<TableRow>
													<TableCell colSpan={7} sx={{ textAlign: "center", py: 3 }}>
														<Typography variant="subtitle1" color="text.secondary">
															Não há registros de logs desta análise
														</Typography>
													</TableCell>
												</TableRow>
											) : (
												visibleLogs.map((log, idx) => {
													const payloadStr = JSON.stringify(log.payload, null, 2);
													return (
														<TableRow
															key={(log as any)?._id ?? `${idx}`}
															sx={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa", "&:hover": { backgroundColor: "#f0f8ff" } }}
														>
															<TableCell>{dayjs(log.createdDate).format("DD/MM/YYYY HH:mm")}</TableCell>
															<TableCell>{log.eventType}</TableCell>
															<TableCell>{log.tableName}</TableCell>
															<TableCell>{(log as any).recordId ?? "-"}</TableCell>
															<TableCell>{log.userId}</TableCell>
															<TableCell>{log.userName}</TableCell>
															<TableCell>
																<Box display="flex" gap={1}>
																	<Tooltip title="Visualizar Payload">
																		<IconButton size="small" onClick={() => handleOpenPayload(log.payload, (log as any)?._id ?? "")}> 
																			<EyeIcon fontSize="small" />
																		</IconButton>
																	</Tooltip>
																	<Tooltip title="Copiar Payload">
																		<IconButton size="small" onClick={() => handleCopy(payloadStr)}>
																			<CopyIcon fontSize="small" />
																		</IconButton>
																	</Tooltip>
																</Box>
															</TableCell>
														</TableRow>
													);
												})
											)}
										</TableBody>
									</Table>
									<Box ref={loaderRef} height={50} textAlign="center" py={2}>
										{visibleLogs.length < filteredLogs.length && <CircularProgress size={20} />}
									</Box>
								</TableContainer>
							)}
						</Box>
					</Box>
				</Fade>
			</Modal>

			<Dialog open={openPayload} onClose={handleClosePayload} maxWidth="md" fullWidth>
				<DialogTitle>
					<Box display="flex" alignItems="center" justifyContent="space-between" pr={4}>
						<Typography fontWeight={600}>Payload: {payloadId}</Typography>
						<Box display="flex" gap={1}>
							<Tooltip title="Copiar payload (JSON formatado)">
								<IconButton onClick={() => handleCopy(payloadContent)}>
									<CopyIcon />
								</IconButton>
							</Tooltip>
							<Tooltip title="Fechar">
								<IconButton onClick={handleClosePayload}>
									<CloseIcon />
								</IconButton>
							</Tooltip>
						</Box>
					</Box>
				</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							maxHeight: "60vh",
							overflow: "auto",
							backgroundColor: "#1e1e1e",
							borderRadius: 2,
							p: 2,
						}}
					>
						{payloadValue && (typeof payloadValue === "object" || Array.isArray(payloadValue)) ? (
							<ReactJson
								src={payloadValue as any}
								displayDataTypes={false}
								quotesOnKeys={false}
								collapsed={2}
								name={false}
								theme="monokai"
							/>
						) : (
							<Box
								sx={{
									color: "#d6e6ff",
									fontFamily: "monospace",
									p: 0,
									whiteSpace: "pre-wrap",
									fontSize: 14,
								}}
							>
								{payloadContent}
							</Box>
						)}
					</Box>
				</DialogContent>
			</Dialog>
		</>
	);
};
